/**
 * Whether the app is in front of the skipper, and how many times it has come back.
 *
 * This exists because of a real morning: the app was left running overnight,
 * Android reclaimed the WebView renderer to free memory (didCrash=false in the
 * death log, JS heap 13 MB of 1965, so nothing of ours was struggling), and
 * reopening sat on a bare grey window for minutes while the activity rebuilt
 * itself. We were simply the fattest thing on the phone and got picked.
 *
 * A chart is a live WebGL map, and it goes on holding its GL context, its tile
 * cache and every texture in it while the app sits in the background doing
 * nothing with any of them. Android counts all of that when it decides who to
 * take. So the answer is to stop holding it: hand the memory back when we go
 * behind, and build it again when we come forward.
 *
 * `resumeCount` is what does that second half. MapView already has a proven path
 * for tearing the style down and putting everything back, the one a palette swap
 * uses, and it fires on a change of style key. Folding this counter into that key
 * means a resume rebuilds through the same code a dusk-to-night switch does,
 * rather than through a second rebuild path nobody exercises.
 */
import { create } from 'zustand';

interface AppActiveState {
  /** True while the app is in the foreground. Starts true: we are being mounted. */
  active: boolean;
  /** Incremented on each background-to-foreground transition. Never decreases. */
  resumeCount: number;
  setActive: (active: boolean) => void;
}

export const useAppActiveStore = create<AppActiveState>((set, get) => ({
  active: true,
  resumeCount: 0,
  setActive: (active) => {
    // Both sources (Capacitor's appStateChange and the page's visibilitychange)
    // fire for the same transition on a phone, so the same value arriving twice
    // must not count as a second resume and rebuild the chart for nothing.
    if (get().active === active) return;
    if (active) set({ active: true, resumeCount: get().resumeCount + 1 });
    else set({ active: false });
  },
}));
